// The body of every Fit and Engagement hover: one sentence built from the real numbers, then just the
// table (category, score × weight, points) and the total. A category with no data carries the amber star;
// a category not yet measured is greyed. No evidence lines, bucket or readiness here. The strategist
// board, Available, the Accounts view, the AE table and the sidebar all draw this one card.

import { useQuery } from '@tanstack/react-query';
import { CardTitle } from '../components/HoverCard';
import { fetchScoreDetail } from '../api/client';
import type { AccountScore, ScoreCategory, ScoreCategoryDetail } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { NOT_MEASURED, SELF_SERVE, SELF_SERVE_PLUS_BOOSTS, boostPointsText, buildBoostBreakdown, buildBreakdown, competitorFlagLine, formatPoints, linesTotal } from './scoreBreakdown';
import type { BoostBreakdown, Breakdown, BreakdownLine, CompetitorFlagLine } from './scoreBreakdown';
import { categoryLabel, formatScore, levelLabel } from './scores';
import { scoreSentence } from './scoreSentence';

export type DetailState = 'loading' | 'failed' | 'ready';

// The reasons and evidence for one account, read when a card opens (the card's body mounts only
// while it is open), so the bulk read never carries them.
export function useScoreDetail(accountId: string) {
  const { getToken } = useAuthContext();
  const query = useQuery({
    queryKey: ['score-detail', accountId],
    queryFn: () => fetchScoreDetail(getToken, accountId),
    staleTime: 60_000,
    retry: false,
  });
  const detailState: DetailState = query.isPending ? 'loading' : query.isError ? 'failed' : 'ready';
  return { detail: query.data?.score ?? null, detailState };
}

// Until the detail arrives, the bulk categories stand in: names and scores, no weights.
function withoutDetail(categories: ScoreCategory[]): ScoreCategoryDetail[] {
  return categories.map(category => ({ ...category, weight: null, reasons: [], evidence: [] }));
}

function LineRow({ line }: { line: BreakdownLine }) {
  if (line.state === 'disabled') {
    return (
      <div className="sbline disabled">
        <span className="sbname">{line.label}</span>
        <span className="sbcalc">{line.disabledReason ?? NOT_MEASURED}</span>
        <span className="sbpoints">—</span>
      </div>
    );
  }
  if (line.state === 'default' && line.weight === 0) {
    return (
      <div className="sbline isdefault">
        <span className="sbname">{line.label}<span className="sbstar"> *</span></span>
        <span className="sbcalc">no data</span>
        <span className="sbpoints">—</span>
      </div>
    );
  }
  const calc = line.weight == null
    ? formatScore(line.score)
    : `${formatScore(line.score)} × ${formatScore(line.weight)}`;
  return (
    <div className={'sbline' + (line.state === 'default' ? ' isdefault' : '')}>
      <span className="sbname">{line.label}{line.state === 'default' ? <span className="sbstar"> *</span> : null}</span>
      <span className="sbcalc">{calc}</span>
      <span className="sbpoints">{line.points == null ? '—' : formatPoints(line.points)}</span>
    </div>
  );
}

// One Engagement line under Self-serve plus boosts: Self-serve counts in full, a boost adds its points
// (score ÷ 100 × its most points), and a category with no data adds nothing.
function BoostLineRow({ line }: { line: BreakdownLine }) {
  if (line.state === 'disabled') return <LineRow line={line} />;
  const isBase = line.category === SELF_SERVE;
  if (line.state === 'default' && line.weight === 0) {
    return (
      <div className="sbline isdefault">
        <span className="sbname">{line.label}<span className="sbstar"> *</span></span>
        <span className="sbcalc">no data</span>
        <span className="sbpoints">—</span>
      </div>
    );
  }
  const calc = isBase ? formatScore(line.score) : `${formatScore(line.score)} of up to +${formatScore(line.weight)}`;
  const points = isBase ? formatPoints(line.points) : boostPointsText(line.points);
  return (
    <div className={'sbline' + (line.state === 'default' ? ' isdefault' : '')}>
      <span className="sbname">{line.label}{line.state === 'default' ? <span className="sbstar"> *</span> : null}</span>
      <span className="sbcalc">{calc}</span>
      <span className="sbpoints">{points}</span>
    </div>
  );
}

function BoostLines({ breakdown, total }: { breakdown: BoostBreakdown; total: number | null }) {
  const disagrees = !breakdown.agrees && breakdown.storedTotal != null;
  return (
    <>
      <div className="sbline sbhead">
        <span className="sbname">Category</span>
        <span className="sbcalc">score (boost: of its most)</span>
        <span className="sbpoints">points</span>
      </div>
      {breakdown.base ? <BoostLineRow line={breakdown.base} /> : null}
      {breakdown.boosts.map(line => <BoostLineRow key={line.category} line={line} />)}
      <div className="sbline sbtotal">
        <span className="sbname">Engagement</span>
        <span className="sbcalc">{breakdown.capped ? 'capped at 100' : ''}</span>
        <span className="sbpoints">{formatPoints(total)}</span>
      </div>
      {disagrees ? (
        <div className="sbwhy sbwarn">
          The lines add to {formatPoints(breakdown.computedTotal)} but the stored score is {formatScore(breakdown.storedTotal)};
          the settings may have changed since it was scored.
        </div>
      ) : null}
    </>
  );
}

// The one total's working, in the total row's middle column: the weights and, when the engine
// divided by them, the scale step. The lines are not summed on a row of their own: summed after
// rounding each to one decimal they could read 56.3 beside a stored 56.2.
function totalWorking(breakdown: Breakdown): string {
  if (!breakdown.weighted) return '';
  const weights = `weights ${formatScore(breakdown.weightSum)}`;
  return breakdown.scaled ? `${weights} · × 100 ÷ ${formatScore(breakdown.weightSum)}` : weights;
}

function DisagreementNote({ breakdown }: { breakdown: Breakdown }) {
  const disagrees = breakdown.weighted && !breakdown.agrees && breakdown.storedTotal != null;
  if (!disagrees) return null;
  return (
    <div className="sbwhy sbwarn">
      The lines add to {formatPoints(breakdown.computedTotal)} but the stored score is {formatScore(breakdown.storedTotal)};
      the weights may have changed since it was scored.
    </div>
  );
}

// The one hard override (GRO-425): a flagged competitor's Fit held to low, or the flag an override lifted.
function CompetitorRow({ line }: { line: CompetitorFlagLine }) {
  return (
    <div className={'sbline sbcompetitor' + (line.lifted ? ' lifted' : '')}>
      <span className="sbname">Competitor flag</span>
      <span className="sbcalc">{line.calc}</span>
      <span className="sbpoints">{line.points == null ? '—' : formatPoints(line.points)}</span>
    </div>
  );
}

export function ScoreCardBody({ accountId, score, kind }: {
  accountId: string;
  score: AccountScore;
  kind: 'fit' | 'engagement';
}) {
  const { detail, detailState } = useScoreDetail(accountId);
  const total = kind === 'fit' ? score.fit_score : score.engagement_score;
  const level = kind === 'fit' ? score.fit_level : score.engagement_level;
  const bulk = kind === 'fit' ? score.fit : score.engagement;
  const categories = detail ? detail[kind] : withoutDetail(bulk);

  // A capped Fit: the lines add up to the uncapped score, and the Competitor line holds it to low.
  const flag = kind === 'fit' ? detail?.competitor_flag : undefined;
  const competitor = competitorFlagLine(flag);
  const breakdown = buildBreakdown(categories, linesTotal(total, flag));
  const boosted = kind === 'engagement' && detail?.engagement_combine === SELF_SERVE_PLUS_BOOSTS;
  const boostBreakdown = boosted ? buildBoostBreakdown(categories, total) : null;

  // The one sentence, from the real numbers; only once the weights have arrived.
  const sentenceLines = boostBreakdown
    ? [...(boostBreakdown.base ? [boostBreakdown.base] : []), ...boostBreakdown.boosts]
    : breakdown.lines;
  const sentence = detail ? scoreSentence(kind, sentenceLines, competitor) : null;
  const title = `${kind === 'fit' ? 'Fit' : 'Engagement'} ${formatScore(total)}${level ? ' · ' + levelLabel(level) : ''}`;
  return (
    <div className="scorebreak">
      <CardTitle>{title}<span className="hcage">scored {score.scored_on}</span></CardTitle>
      {sentence ? <p className="sbsentence">{sentence}</p> : null}
      <div className="sblines">
        {boostBreakdown ? <BoostLines breakdown={boostBreakdown} total={total} /> : (
          <>
            <div className="sbline sbhead">
              <span className="sbname">Category</span>
              <span className="sbcalc">score × weight</span>
              <span className="sbpoints">points</span>
            </div>
            {breakdown.lines.map(line => <LineRow key={line.category} line={line} />)}
            {competitor ? <CompetitorRow line={competitor} /> : null}
            <div className="sbline sbtotal">
              <span className="sbname">{kind === 'fit' ? 'Fit' : 'Engagement'}</span>
              <span className="sbcalc">{totalWorking(breakdown)}</span>
              <span className="sbpoints">{formatPoints(total)}</span>
            </div>
            <DisagreementNote breakdown={breakdown} />
          </>
        )}
      </div>
      {detailState === 'loading' ? <div className="sbwhy">Loading the breakdown…</div> : null}
      {detailState === 'failed' ? <div className="sbwhy sbwarn">The breakdown could not be loaded.</div> : null}
      {detail && detail.failed_categories.length
        ? <div className="sbwhy sbwarn">Scoring failed for {detail.failed_categories.map(categoryLabel).join(', ')}; the default was used.</div>
        : null}
    </div>
  );
}
